(() => {
  'use strict';

  const CONFIG = window.RachowLeasingConfig || {};
  const applicationUrl = CONFIG.applicationUrl || '/pages/leasing-anfrage';
  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let state = readState(document);
  let refreshTimer = null;
  let renderTimer = null;
  let observer = null;
  let maintenanceTimer = null;
  const observedRoots = new WeakSet();

  function allOpenRoots() {
    const roots = [document];
    for (let index = 0; index < roots.length; index += 1) {
      roots[index].querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot && !roots.includes(element.shadowRoot)) {
          roots.push(element.shadowRoot);
        }
      });
    }
    return roots;
  }

  function readState(scope) {
    const element = scope && scope.querySelector && scope.querySelector('#rachow-leasing-cart-state');
    const json = element && element.querySelector('[data-leasing-cart-json]');
    if (!json) return null;

    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      console.warn('[Leasing] Warenkorbstatus konnte nicht gelesen werden.', error);
      return null;
    }
  }

  function isVisible(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) !== 0 &&
      rect.width > 0 &&
      rect.height > 0;
  }

  function isCheckoutControl(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('.rachow-leasing-cart-cta')) return false;

    const name = (element.getAttribute('name') || '').toLowerCase();
    const id = (element.id || '').toLowerCase();
    const className = typeof element.className === 'string' ? element.className.toLowerCase() : '';
    const text = [
      element.textContent || '',
      element.getAttribute('value') || '',
      element.getAttribute('aria-label') || ''
    ].join(' ').toLowerCase();
    const href = (element.getAttribute('href') || '').toLowerCase();

    if (name === 'checkout' || id === 'checkout' || className.includes('cart__checkout-button')) return true;
    if (href.includes('/checkout')) return true;

    const explicitLabel = /sicher\s+zur\s+kasse|zur\s+kasse|auschecken|checkout|jetzt\s+bezahlen/.test(text);
    const checkoutClass = className.includes('checkout') || element.hasAttribute('data-checkout-button');
    return explicitLabel || (checkoutClass && /checkout|kasse|bezahlen/.test(text));
  }

  function checkoutControls() {
    const selectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      '#checkout',
      '#CartDrawer-Checkout',
      '.cart__checkout-button',
      '[data-checkout-button]',
      'button[class*="checkout" i]',
      'a[class*="checkout" i]',
      'a[href*="/checkout"]',
      'button',
      'input[type="submit"]'
    ];

    const controls = new Set();
    allOpenRoots().forEach((root) => {
      selectors.forEach((selector) => {
        root.querySelectorAll(selector).forEach((element) => {
          if (isCheckoutControl(element) && isVisible(element)) controls.add(element);
        });
      });
    });
    return Array.from(controls);
  }

  function allCtas() {
    const ctas = [];
    allOpenRoots().forEach((root) => {
      root.querySelectorAll('[data-rachow-leasing-cta]').forEach((element) => ctas.push(element));
    });
    return ctas;
  }

  function observeOpenRoots() {
    if (!observer) return;

    allOpenRoots().forEach((root) => {
      const target = root === document ? document.documentElement : root;
      if (!target || observedRoots.has(target)) return;

      observer.observe(target, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'open', 'aria-hidden', 'aria-expanded']
      });
      observedRoots.add(target);
    });
  }

  function createCta(checkoutControl) {
    const wrapper = document.createElement('div');
    wrapper.className = 'rachow-leasing-cart-cta';
    wrapper.setAttribute('data-rachow-leasing-cta', 'true');

    const link = document.createElement('a');
    link.className = 'rachow-leasing-cart-button';
    link.href = applicationUrl;
    link.textContent = 'Leasing-Anfrage starten';

    const hint = document.createElement('p');
    hint.className = 'rachow-leasing-cart-hint';
    hint.textContent = 'Fahrrad und gewünschte Produkte unverbindlich als Leasing anfragen.';

    wrapper.append(link, hint);
    checkoutControl.insertAdjacentElement('afterend', wrapper);
    return wrapper;
  }

  function ensureCta(checkoutControl) {
    const next = checkoutControl.nextElementSibling;
    if (next && next.matches('[data-rachow-leasing-cta]')) return next;

    const parent = checkoutControl.parentElement;
    if (parent) {
      const existing = Array.from(parent.children).find((child) => child.matches && child.matches('[data-rachow-leasing-cta]'));
      if (existing) {
        checkoutControl.insertAdjacentElement('afterend', existing);
        return existing;
      }
    }

    return createCta(checkoutControl);
  }

  function render() {
    observeOpenRoots();

    if (!state || !state.hasBike || Number(state.itemCount || 0) < 1) {
      allCtas().forEach((element) => element.remove());
      return;
    }

    checkoutControls().forEach((checkoutControl) => {
      ensureCta(checkoutControl);
    });
  }

  async function refreshState() {
    const separator = rootUrl.includes('?') ? '&' : '?';
    const url = `${rootUrl}${separator}section_id=leasing-cart-state&leasing_ts=${Date.now()}`;

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextState = readState(parsed);
      if (nextState) state = nextState;
    } catch (error) {
      const inlineState = readState(document);
      if (inlineState) state = inlineState;
      console.warn('[Leasing] Warenkorbstatus konnte nicht aktualisiert werden.', error);
    }

    render();
  }

  function mutationLooksCartRelated(mutations) {
    const pattern = /cart|warenkorb|checkout|kasse|drawer/i;
    return mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target && pattern.test(`${target.id || ''} ${typeof target.className === 'string' ? target.className : ''}`)) return true;

      return Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some((node) => {
        if (!(node instanceof Element)) return false;
        const signature = `${node.id || ''} ${typeof node.className === 'string' ? node.className : ''} ${node.textContent || ''}`;
        return pattern.test(signature) || Boolean(node.querySelector && node.querySelector('[name="checkout"], .cart__checkout-button, [class*="checkout" i]'));
      });
    });
  }

  function scheduleRender(delay = 60) {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(render, delay);
  }

  function scheduleRefresh(delay = 250) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshState, delay);
  }

  function startMaintenance() {
    window.clearInterval(maintenanceTimer);
    maintenanceTimer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      observeOpenRoots();
      render();
    }, 600);
  }

  function boot() {
    state = readState(document) || state;

    observer = new MutationObserver((mutations) => {
      observeOpenRoots();
      scheduleRender(40);
      if (mutationLooksCartRelated(mutations)) scheduleRefresh(300);
    });

    observeOpenRoots();
    render();
    scheduleRefresh(50);
    startMaintenance();
  }

  document.addEventListener('click', () => {
    scheduleRender(80);
    scheduleRender(350);
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleRender(20);
      scheduleRefresh(100);
    }
  });

  window.addEventListener('pageshow', () => {
    scheduleRender(20);
    scheduleRefresh(100);
  });

  ['cart:updated', 'cart:refresh', 'cart-drawer:updated', 'shopify:section:load', 'product:added'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleRefresh(80));
    window.addEventListener(eventName, () => scheduleRefresh(80));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
